import React from 'react';
import { KpiRow } from 'types/machine-health';
import { useTranslation } from 'react-i18next';
import { ToolTipWrapper } from 'components/machine-health/configurator/WidgetTable/Dropdown';
import ErrorAlt6 from 'icons/indexed-icons/ErrorAlt6';
import ActiveRecipe from 'icons/indexed-icons/ActiveRecipe';
import theme from 'themes';
import {
  ActualValueMsg,
  GridContainer,
  HeaderCell,
  InnerHeader,
  RowItem,
  SectionHeading,
  StyledTableContainer,
  ToolTipTextMsg
} from './RowRenderer.elements';

type Props = {
  rows: KpiRow[];
  containerHeight?: number;
  rowHeight?: number;
  onClick?: () => void;
  isCustomRowHeight?: boolean;
  customRowHeight?: string;
  lineView?: boolean;
};

const groupItemsByKey = (data: KpiRow[]) => {
  const groups: { [key: string]: KpiRow[] } = {};
  const unmatchedGroup: KpiRow[] = [];

  data.forEach(item => {
    let matched = false;

    for (const groupKey in groups) {
      if (item.value && item.value.key.startsWith(groupKey)) {
        groups[groupKey].push(item);
        matched = true;
        break;
      }
    }

    if (!matched) {
      if (item.value && item.value.key !== undefined) {
        if (!groups[item.value.key]) {
          groups[item.value.key] = [];
        }
        groups[item.value.key].push(item);
      }
    }
  });

  // Check for groups with only one item and move them to unmatchedGroup
  for (const groupKey in groups) {
    if (groups[groupKey].length === 1) {
      unmatchedGroup.push(...groups[groupKey]);
      delete groups[groupKey];
    } else {
      // Remove the main key object from the group
      groups[groupKey] = groups[groupKey].filter(item => item.value.key !== groupKey);
    }
  }

  if (unmatchedGroup.length > 0) {
    groups['unmatched'] = unmatchedGroup;
  }

  return groups;
}

export const generateTooltip = (text: string): JSX.Element => {
  if (text.length < 16) {
    return <ActualValueMsg>{text}</ActualValueMsg>;
  } else {
    return (
      <ToolTipWrapper tooltipMessage={text as string}>
        <ToolTipTextMsg>{`${text.substring(0, 10)}...`}</ToolTipTextMsg>
      </ToolTipWrapper>
    );
  }
};

export const NewRowRenderer = ({ rows, lineView }: Props): JSX.Element => {
  const { t } = useTranslation(['mh']);
  const displayedHeaders = new Set();
  const groupedRows = groupItemsByKey(rows);

  return (
    <StyledTableContainer disableMaxHeight={lineView}>
      <GridContainer>
        {groupedRows['unmatched']?.length > 0 && (
          <>
            <HeaderCell className="first-header"></HeaderCell>
            <HeaderCell>{t('target')}</HeaderCell>
            <HeaderCell>{t('Actual Value')}</HeaderCell>
          </>
        )}

        {rows?.map((pair, index) => {
          const { content, targetValue, clickable, isTargetMet } = pair?.value;
          const showHeaderAgain = !displayedHeaders.has(pair.label) && clickable === undefined;

          if (showHeaderAgain) {
            displayedHeaders.add(pair.label);
            return (
              <>
                { !lineView && (
                  <SectionHeading>
                    <ActiveRecipe color={theme.colors.cyan} /> {t(pair?.label)}
                  </SectionHeading>
                )}
                <InnerHeader>
                  {lineView ? (<div className="inner-header first-header">{t(pair?.label)}</div>)
                  : (<div className="inner-header first-header"></div>)
                  }
                  <div className="inner-header">{t('target')}</div>
                  <div className="inner-header">{t('Actual Value')}</div>
                </InnerHeader>
              </>
            );
          }
          return (
            <>
              <RowItem className="first-row-item">{t(pair?.label || '')}</RowItem>
              <RowItem>{targetValue !== undefined ? targetValue : '-'}</RowItem>
              <RowItem
                className={`row-item ${
                  isTargetMet !== undefined && !isTargetMet ? 'error-color' : ''
                }`}
              >
                {isTargetMet !== undefined && !isTargetMet && (
                  <ErrorAlt6 color={theme.colors.error200} />
                )}
                {content && generateTooltip((content as string) || '-')}
              </RowItem>
            </>
          );
        })}
      </GridContainer>
    </StyledTableContainer>
  );
};
